import { useState } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import type { DragEndEvent } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  GripVertical,
  Lock,
  ChevronDown,
  ChevronRight,
  Pencil,
  Check,
  X,
  Trash2,
  Plus,
  Type,
  MousePointerClick,
  Minus,
  ImageIcon,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ImageUpload } from "@/components/ui/image-upload";
import { ContentBlock, ContentBlockType, getDefaultBlockLabel, EmailTemplateType } from "@/types/email-templates";

// Built-in block types that can't be removed. Includes the value-layer blocks
// auto-injected by the edge function renderers — users can toggle/rename/reorder
// them, but never delete them (the renderer would just re-inject on next save).
const BUILT_IN_BLOCK_TYPES: ContentBlockType[] = [
  // Conversation report — original
  'metrics', 'lead_info', 'topics', 'insights', 'transcript', 'summary', 'mobile_app_promo',
  // Conversation report — value-layer
  'goal_funnel', 'next_steps', 'top_call_comparison', 'suggested_follow_up', 'week_in_review',
  // Customer report — value-layer
  'get_more_help', 'key_takeaways', 'highlights', 'useful_links', 'time_saved',
  // Meeting recap
  'attendees', 'key_insights', 'decisions', 'action_plan', 'recommendations', 'sentiment',
];

// Custom block types users can add
const CUSTOM_BLOCK_OPTIONS: Array<{ type: ContentBlockType; label: string; icon: typeof Type; description: string }> = [
  { type: 'custom_text', label: 'Text Section', icon: Type, description: 'Add a custom heading and body text' },
  { type: 'cta_button', label: 'Call to Action', icon: MousePointerClick, description: 'Add a clickable button' },
  { type: 'divider', label: 'Divider', icon: Minus, description: 'Add a horizontal line separator' },
  { type: 'image', label: 'Image', icon: ImageIcon, description: 'Add an image from a URL' },
];

const MAX_CUSTOM_BLOCKS = 10;

interface BlockEditorProps {
  blocks: ContentBlock[];
  templateType: EmailTemplateType;
  onToggleBlock: (blockId: string, enabled: boolean) => void;
  onReorderBlocks: (blocks: ContentBlock[]) => void;
  onUpdateLabel: (blockId: string, label: string) => void;
  onUpdateSettings: (blockId: string, settings: Record<string, any>) => void;
  onAddBlock: (block: ContentBlock) => void;
  onRemoveBlock: (blockId: string) => void;
}

// --- Block Settings Panels ---

const CustomTextSettings = ({
  block,
  onUpdateSettings,
}: {
  block: ContentBlock;
  onUpdateSettings: (blockId: string, settings: Record<string, any>) => void;
}) => {
  const settings = block.settings || {};

  return (
    <div className="space-y-3 pt-2">
      <div className="space-y-1.5">
        <Label className="text-xs">Title (optional)</Label>
        <Input
          value={settings.title || ""}
          onChange={(e) => onUpdateSettings(block.id, { title: e.target.value })}
          placeholder="Section title..."
          className="h-8 text-sm"
        />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">Body</Label>
        <Textarea
          value={settings.body || ""}
          onChange={(e) => onUpdateSettings(block.id, { body: e.target.value })}
          placeholder="Enter your content..."
          className="text-sm min-h-[80px] resize-y"
        />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">Alignment</Label>
        <Select
          value={settings.alignment || "left"}
          onValueChange={(val) => onUpdateSettings(block.id, { alignment: val })}
        >
          <SelectTrigger className="h-8 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="left">Left</SelectItem>
            <SelectItem value="center">Center</SelectItem>
            <SelectItem value="right">Right</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

const CtaButtonSettings = ({
  block,
  onUpdateSettings,
}: {
  block: ContentBlock;
  onUpdateSettings: (blockId: string, settings: Record<string, any>) => void;
}) => {
  const settings = block.settings || {};

  return (
    <div className="space-y-3 pt-2">
      <div className="space-y-1.5">
        <Label className="text-xs">Button Text</Label>
        <Input
          value={settings.text || ""}
          onChange={(e) => onUpdateSettings(block.id, { text: e.target.value })}
          placeholder="e.g. Book Follow-up"
          className="h-8 text-sm"
        />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">URL</Label>
        <Input
          value={settings.url || ""}
          onChange={(e) => onUpdateSettings(block.id, { url: e.target.value })}
          placeholder="https://..."
          className="h-8 text-sm"
        />
      </div>
    </div>
  );
};

const ImageSettings = ({
  block,
  onUpdateSettings,
}: {
  block: ContentBlock;
  onUpdateSettings: (blockId: string, settings: Record<string, any>) => void;
}) => {
  const settings = block.settings || {};
  const [uploadMode, setUploadMode] = useState<"upload" | "url">("upload");

  const handleUrlChange = (url: string) => {
    // Reject base64 data URIs - always use external URLs
    if (url.startsWith('data:')) {
      return;
    }
    onUpdateSettings(block.id, { url });
  };

  return (
    <div className="space-y-3 pt-2">
      <div className="space-y-1.5">
        <Label className="text-xs">Image</Label>
        <Tabs value={uploadMode} onValueChange={(value) => setUploadMode(value as "upload" | "url")}>
          <TabsList className="grid w-full grid-cols-2 h-8">
            <TabsTrigger value="upload" className="text-xs">Upload</TabsTrigger>
            <TabsTrigger value="url" className="text-xs">URL</TabsTrigger>
          </TabsList>

          <TabsContent value="upload" className="space-y-1.5">
            <ImageUpload
              bucket="genie-images"
              value={settings.url}
              onChange={(url) => onUpdateSettings(block.id, { url })}
              label=""
              className="w-full"
            />
          </TabsContent>

          <TabsContent value="url" className="space-y-1.5">
            <Input
              value={settings.url || ""}
              onChange={(e) => handleUrlChange(e.target.value)}
              placeholder="https://..."
              className="h-8 text-sm"
            />
          </TabsContent>
        </Tabs>
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">Alt Text</Label>
        <Input
          value={settings.altText || ""}
          onChange={(e) => onUpdateSettings(block.id, { altText: e.target.value })}
          placeholder="Describe the image..."
          className="h-8 text-sm"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs">Max Width (px)</Label>
          <Input
            type="number"
            value={settings.maxWidth || 540}
            onChange={(e) => onUpdateSettings(block.id, { maxWidth: parseInt(e.target.value) || 540 })}
            className="h-8 text-sm"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Alignment</Label>
          <Select
            value={settings.alignment || "center"}
            onValueChange={(val) => onUpdateSettings(block.id, { alignment: val })}
          >
            <SelectTrigger className="h-8 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="left">Left</SelectItem>
              <SelectItem value="center">Center</SelectItem>
              <SelectItem value="right">Right</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
};

// Map block types to their settings panels
const BLOCK_SETTINGS_PANELS: Partial<Record<ContentBlockType, typeof CustomTextSettings>> = {
  custom_text: CustomTextSettings,
  cta_button: CtaButtonSettings,
  image: ImageSettings,
};

// --- Sortable Block Item ---

interface SortableBlockItemProps {
  block: ContentBlock;
  isCustom: boolean;
  templateType: EmailTemplateType;
  onToggle: (blockId: string, enabled: boolean) => void;
  onUpdateLabel: (blockId: string, label: string) => void;
  onUpdateSettings: (blockId: string, settings: Record<string, any>) => void;
  onRemove: (blockId: string) => void;
}

const SortableBlockItem = ({
  block,
  isCustom,
  templateType,
  onToggle,
  onUpdateLabel,
  onUpdateSettings,
  onRemove,
}: SortableBlockItemProps) => {
  const [isEditingLabel, setIsEditingLabel] = useState(false);
  const [editValue, setEditValue] = useState(block.label || "");
  const [isExpanded, setIsExpanded] = useState(false);

  // The signature depends on genie/team branding and reads as a sign-off —
  // it must always render last (the renderers pin it there server-side too,
  // see pinSignatureLast in supabase/functions/_shared/email/templates/*.ts), so
  // dragging it out of that position is never allowed here.
  const isSignature = block.type === "signature";

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: block.id, disabled: isSignature });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const defaultLabel = getDefaultBlockLabel(block.type as ContentBlockType, templateType);
  const displayLabel = block.label || defaultLabel;

  const SettingsPanel = BLOCK_SETTINGS_PANELS[block.type as ContentBlockType];
  const hasSettings = !!SettingsPanel;

  const handleSaveLabel = () => {
    onUpdateLabel(block.id, editValue);
    setIsEditingLabel(false);
  };

  const handleCancelLabel = () => {
    setEditValue(block.label || "");
    setIsEditingLabel(false);
  };

  const handleStartEdit = () => {
    setEditValue(block.label || defaultLabel);
    setIsEditingLabel(true);
  };

  // Find the icon for custom block types
  const customOption = CUSTOM_BLOCK_OPTIONS.find((o) => o.type === block.type);
  const BlockIcon = customOption?.icon;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`rounded-lg border bg-white transition-colors ${
        isDragging ? "shadow-lg border-primary/30" : "border-border hover:border-primary/20"
      } ${!block.enabled ? "opacity-60" : ""}`}
    >
      {/* Main row */}
      <div className="flex items-center gap-2 p-3">
        {isSignature ? (
          <div
            className="text-muted-foreground/40 p-0.5 flex-shrink-0"
            title="The signature always stays last"
          >
            <Lock className="h-4 w-4" />
          </div>
        ) : (
          <button
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground p-0.5 touch-none flex-shrink-0"
            tabIndex={-1}
          >
            <GripVertical className="h-4 w-4" />
          </button>
        )}

        {/* Expand/collapse for blocks with settings */}
        {hasSettings ? (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-0.5 text-muted-foreground hover:text-foreground flex-shrink-0"
          >
            {isExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          </button>
        ) : (
          <div className="w-[22px] flex-shrink-0" />
        )}

        {BlockIcon && <BlockIcon className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />}

        <div className="flex-1 min-w-0">
          {isEditingLabel ? (
            <div className="flex items-center gap-1.5">
              <Input
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                className="h-7 text-sm py-0"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSaveLabel();
                  if (e.key === "Escape") handleCancelLabel();
                }}
              />
              <button onClick={handleSaveLabel} className="p-0.5 text-green-600 hover:text-green-700">
                <Check className="h-3.5 w-3.5" />
              </button>
              <button onClick={handleCancelLabel} className="p-0.5 text-muted-foreground hover:text-foreground">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-medium truncate">{displayLabel}</span>
              {block.label && block.label !== defaultLabel && (
                <span className="text-[10px] text-muted-foreground">({defaultLabel})</span>
              )}
              <button
                onClick={handleStartEdit}
                className="p-0.5 text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Pencil className="h-3 w-3" />
              </button>
            </div>
          )}
        </div>

        {isCustom && (
          <button
            onClick={() => onRemove(block.id)}
            className="p-1 text-muted-foreground hover:text-destructive transition-colors flex-shrink-0"
            title="Remove section"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}

        <Switch
          checked={block.enabled}
          onCheckedChange={(checked) => onToggle(block.id, checked)}
          className="ml-auto flex-shrink-0"
        />
      </div>

      {/* Expandable settings panel */}
      {hasSettings && isExpanded && SettingsPanel && (
        <div className="px-3 pb-3 border-t border-border/50 mx-3 pt-1">
          <SettingsPanel block={block} onUpdateSettings={onUpdateSettings} />
        </div>
      )}
    </div>
  );
};

// --- Main BlockEditor ---

const BlockEditor = ({
  blocks,
  templateType,
  onToggleBlock,
  onReorderBlocks,
  onUpdateLabel,
  onUpdateSettings,
  onAddBlock,
  onRemoveBlock,
}: BlockEditorProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const sortedBlocks = [...blocks].sort((a, b) => a.order - b.order);
  const customBlockCount = blocks.filter((b) => !BUILT_IN_BLOCK_TYPES.includes(b.type as ContentBlockType)).length;
  const canAddMore = customBlockCount < MAX_CUSTOM_BLOCKS;

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = sortedBlocks.findIndex((b) => b.id === active.id);
    const newIndex = sortedBlocks.findIndex((b) => b.id === over.id);

    // useSortable's disabled flag on the signature item stops IT from being
    // dragged, but dnd-kit still lets other items drop past its position —
    // clamp the target so nothing can be reordered after (or onto) it, since
    // the signature must always render last (see pinSignatureLast in
    // supabase/functions/_shared/email/templates/*.ts).
    const signatureIndex = sortedBlocks.findIndex((b) => b.type === "signature");
    const clampedNewIndex =
      signatureIndex === -1 ? newIndex : Math.min(newIndex, signatureIndex - 1);
    if (clampedNewIndex < 0 || oldIndex === clampedNewIndex) return;

    const reordered = arrayMove(sortedBlocks, oldIndex, clampedNewIndex);
    onReorderBlocks(reordered);
  };

  const handleAddBlock = (type: ContentBlockType) => {
    const id = `${type}_${Date.now()}`;
    const newBlock: ContentBlock = {
      id,
      type,
      enabled: true,
      order: blocks.length,
      settings: type === 'cta_button' ? { text: 'Click Here', url: '' } : {},
    };
    onAddBlock(newBlock);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Report Sections</CardTitle>
        <CardDescription>
          Drag to reorder, toggle visibility, click the pencil to rename. Expand custom sections to configure their content.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={sortedBlocks.map((b) => b.id)}
            strategy={verticalListSortingStrategy}
          >
            {sortedBlocks.map((block) => {
              const isCustom = !BUILT_IN_BLOCK_TYPES.includes(block.type as ContentBlockType);
              return (
                <div key={block.id} className="group">
                  <SortableBlockItem
                    block={block}
                    isCustom={isCustom}
                    templateType={templateType}
                    onToggle={onToggleBlock}
                    onUpdateLabel={onUpdateLabel}
                    onUpdateSettings={onUpdateSettings}
                    onRemove={onRemoveBlock}
                  />
                </div>
              );
            })}
          </SortableContext>
        </DndContext>

        {/* Add Block Button */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="w-full mt-3 border-dashed"
              disabled={!canAddMore}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Section
              {!canAddMore && <span className="ml-1 text-muted-foreground">(max {MAX_CUSTOM_BLOCKS})</span>}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="center" className="w-64">
            {CUSTOM_BLOCK_OPTIONS.map(({ type, label, icon: Icon, description }) => (
              <DropdownMenuItem
                key={type}
                onClick={() => handleAddBlock(type)}
                className="flex items-start gap-3 py-2.5"
              >
                <Icon className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                <div>
                  <div className="font-medium text-sm">{label}</div>
                  <div className="text-xs text-muted-foreground">{description}</div>
                </div>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </CardContent>
    </Card>
  );
};

export default BlockEditor;
